import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import AddStaffForm from './parts/AddStaffForm';
import EditStaffForm, { EditableStaff } from './parts/EditStaffForm';
import ViewStaffModal, { StaffDetail } from './parts/ViewStaffModal';
import ResetPasswordModal, { ResettableStaff } from './parts/ResetPasswordModal';
import {
  Building2,
  Calendar,
  Contact,
  Eye,
  Inbox,
  KeyRound,
  LayoutGrid,
  List,
  Mail,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
  User,
  X,
} from 'lucide-react';
import CustomPaginator from '../../components/GlobalComponents/CustomPaginator';
import StaffListSkeleton from '../../components/skeletons/StaffListSkeleton';
import { G, C } from '../../components/GlobalComponents/uiTokens';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};
const LABEL: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: C.sub,
};

// ── Types (match GET /api/v1/staff response) ──────────────────────────────────
interface StaffMember {
  id: string;
  email: string | null;
  name: string | null;
  gender: string | null;
  address: string | null;
  phone: string | null;
  role: string | null;
  status: number | null;
  agency_id: string | null;
  agency_name: string | null;
  created_by_id: string | null;
  created_by_email: string | null;
  created_at: string | null;
}

interface ApiResponse {
  success: boolean;
  data: StaffMember[];
  meta: { total: number; page: number; limit: number; pages: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
};

const AVCOLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777', '#ea580c'];
const avc = (s: string | null) => {
  if (!s) return AVCOLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVCOLORS[Math.abs(h) % AVCOLORS.length];
};

const displayName = (row: StaffMember) =>
  (row.name && row.name.trim()) || row.email?.split('@')[0] || '-';

const ini2 = (row: StaffMember) => {
  const n = displayName(row);
  return n.substring(0, 2).toUpperCase();
};

// ─────────────────────────────────────────────────────────────────────────────

const Members: React.FC = () => {
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 0 });
  const [selectedMembers, setSelectedMembers] = useState<StaffMember[] | null>(null);
  const [agencyFilter, setAgencyFilter] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Modal state: view / edit / reset password
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [editingStaff, setEditingStaff] = useState<EditableStaff | null>(null);
  const [resettingStaff, setResettingStaff] = useState<ResettableStaff | null>(null);

  // Server-side list: page, limit, search, agency_id (superadmin)
  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const p = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (agencyFilter.trim()) p.set('agency_id', agencyFilter.trim());
      if (search.trim()) p.set('search', search.trim());

      const res = await apiClient.get<ApiResponse>(`/v1/staff?${p}`);
      if (res.data.success) {
        setMembers(res.data.data ?? []);
        setPagination(res.data.meta ?? { total: 0, page: 1, limit: 20, pages: 0 });
      } else {
        setError('Failed to load members');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, agencyFilter]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSearch = () => {
    setSearch(tempSearch);
    setPage(1);
  };

  const handleReset = () => {
    setTempSearch('');
    setSearch('');
    setAgencyFilter('');
    setPage(1);
  };

  const handleDelete = async (row: StaffMember) => {
    const label = displayName(row);
    if (
      !window.confirm(
        `Delete staff member "${label}"?\n\nLeads assigned to them will be unassigned. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setDeletingId(row.id);
      setError(null);
      await apiClient.delete(`/v1/staff/${row.id}`);
      setSelectedMembers((prev) => (prev ? prev.filter((m) => m.id !== row.id) : null));
      await fetchMembers();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to delete staff');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (row: StaffMember) => {
    const newStatus = row.status === 0 ? 1 : 0;
    try {
      setTogglingId(row.id);
      setError(null);
      await apiClient.patch(`/v1/staff/${row.id}/status`, { status: newStatus });
      await fetchMembers();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Col styles ────────────────────────────────────────────────────────────
  const CH: React.CSSProperties = {
    background: '#fafbfb',
    color: '#9ca3af',
    fontSize: '0.64rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${G.soft}`,
    padding: '10px 13px',
    letterSpacing: '0.5px',
  };
  const CB: React.CSSProperties = {
    padding: '10px 13px',
    borderBottom: `1px solid ${G.softer}`,
  };

  // ── Status toggle (shared by table + card views) ─────────────────────────
  const StatusToggle: React.FC<{ row: StaffMember; small?: boolean }> = ({ row, small }) => {
    const active = row.status !== 0;
    const isToggling = togglingId === row.id;
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        disabled={isToggling}
        title={active ? 'Click to deactivate' : 'Click to activate'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3.7px',
          padding: small ? '1.8px 7.4px' : '2.8px 9.2px',
          borderRadius: '20px',
          fontSize: small ? '0.6rem' : '0.63rem',
          fontWeight: 700,
          border: `1px solid ${active ? 'rgba(22,163,74,0.25)' : G.line}`,
          background: active ? 'rgba(22,163,74,0.1)' : 'rgba(156,163,175,0.15)',
          color: active ? '#15803d' : C.sub,
          cursor: isToggling ? 'not-allowed' : 'pointer',
          opacity: isToggling ? 0.6 : 1,
          transition: 'all 0.2s',
        }}
      >
        <Power size={small ? 9 : 10} />
        {isToggling ? '...' : active ? 'Active' : 'Inactive'}
      </button>
    );
  };

  // ── Templates ─────────────────────────────────────────────────────────────
  const memberTpl = (row: StaffMember) => {
    const c = avc(row.email || row.name);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8.3px' }}>
        <div
          style={{
            width: '31.4px',
            height: '31.4px',
            borderRadius: '50%',
            background: c,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.66rem',
            fontWeight: '700',
            boxShadow: `0 2px 6px ${c}55`,
          }}
        >
          {ini2(row)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span
            style={{
              color: C.teal,
              fontSize: '0.76rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '166px',
            }}
          >
            {displayName(row)}
          </span>
          <span
            style={{
              fontSize: '0.63rem',
              color: C.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '166px',
            }}
          >
            {row.email || '-'}
          </span>
        </div>
      </div>
    );
  };

  const roleTpl = (row: StaffMember) => (
    <span
      style={{
        padding: '2.8px 9.2px',
        borderRadius: '20px',
        fontSize: '0.63rem',
        fontWeight: '700',
        textTransform: 'capitalize',
        display: 'inline-block',
        background: 'rgba(17,85,80,0.08)',
        color: C.teal,
        border: '1px solid rgba(17,85,80,0.2)',
      }}
    >
      <User size={10} style={{ marginRight: '3.7px' }} />
      {row.role || 'staff'}
    </span>
  );

  const statusTpl = (row: StaffMember) => <StatusToggle row={row} />;

  const agencyTpl = (row: StaffMember) => {
    if (!row.agency_name) return <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3.7px',
          padding: '2.8px 7.4px',
          borderRadius: '6.5px',
          fontSize: '0.63rem',
          fontWeight: '600',
          background: C.limeDim,
          color: '#4a5e00',
          border: `1px solid ${C.limeBorder}`,
        }}
      >
        <Building2 size={10} />
        {row.agency_name}
      </span>
    );
  };

  const createdByTpl = (row: StaffMember) => {
    if (!row.created_by_email) return <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>;
    const c = avc(row.created_by_email);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4.6px' }}>
        <div
          style={{
            width: '20.3px',
            height: '20.3px',
            borderRadius: '50%',
            background: c,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.52rem',
            fontWeight: '700',
          }}
        >
          {row.created_by_email.split('@')[0].substring(0, 2).toUpperCase()}
        </div>
        <span
          style={{
            fontSize: '0.68rem',
            color: C.sub,
            maxWidth: '111px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.created_by_email.split('@')[0]}
        </span>
      </div>
    );
  };

  const actionTpl = (row: StaffMember) => (
    <div style={{ display: 'flex', gap: '2.8px', justifyContent: 'center' }}>
      {[
        {
          icon: Eye,
          title: 'View',
          fn: () => setViewingId(row.id),
          hbg: 'rgba(17,85,80,0.08)',
          hcol: C.teal,
          hbor: 'rgba(17,85,80,0.2)',
          disabled: false,
        },
        {
          icon: Pencil,
          title: 'Edit',
          fn: () => setEditingStaff(row),
          hbg: C.limeDim,
          hcol: '#4a5e00',
          hbor: C.limeBorder,
          disabled: false,
        },
        {
          icon: KeyRound,
          title: 'Reset Password',
          fn: () => setResettingStaff(row),
          hbg: 'rgba(59,130,246,0.08)',
          hcol: '#1d4ed8',
          hbor: 'rgba(59,130,246,0.25)',
          disabled: false,
        },
        {
          icon: Trash2,
          title: 'Delete',
          fn: () => handleDelete(row),
          hbg: 'rgba(220,38,38,0.08)',
          hcol: '#dc2626',
          hbor: 'rgba(220,38,38,0.25)',
          disabled: deletingId === row.id,
        },
      ].map((btn) => (
        <button
          key={btn.title}
          onClick={btn.fn}
          title={btn.title}
          disabled={btn.disabled}
          style={{
            width: '25px',
            height: '25px',
            background: 'transparent',
            border: '1px solid transparent',
            borderRadius: '5.5px',
            cursor: btn.disabled ? 'not-allowed' : 'pointer',
            opacity: btn.disabled ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.sub,
            padding: 0,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (btn.disabled) return;
            e.currentTarget.style.background = btn.hbg;
            e.currentTarget.style.borderColor = btn.hbor;
            e.currentTarget.style.color = btn.hcol;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.color = C.sub;
          }}
        >
          {React.createElement(btn.icon, {
            size: 14,
            strokeWidth: 2,
            style: { flexShrink: 0, display: 'block' },
          })}
        </button>
      ))}
    </div>
  );

  // ── Cards ─────────────────────────────────────────────────────────────────
  const rowLabel: React.CSSProperties = {
    color: C.muted,
    fontSize: '0.63rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  };
  const rowWrap: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  // ── Shared empty state (list + cards) ─────────────────────────────────────
  const emptyState = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '36px 0',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'rgba(17,85,80,0.07)',
          border: '1px solid rgba(17,85,80,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Inbox size={22} color={C.teal} />
      </div>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: C.teal }}>No members found</span>
    </div>
  );

  const CardsView = () =>
    members.length === 0 ? (
      emptyState
    ) : (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(221px,1fr))',
        gap: '11px',
        padding: '14.8px 16.6px',
      }}
    >
      {members.map((member) => {
        const c = avc(member.email || member.name);
        return (
          <div
            key={member.id}
            onClick={() => setViewingId(member.id)}
            style={{
              ...SHELL,
              borderRadius: '11px',
              padding: '3.7px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.boxShadow = '0 5px 18px rgba(17,85,80,0.10)';
              el.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.boxShadow = 'none';
              el.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ ...INNER, borderRadius: '8.3px', padding: '12.9px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9.2px',
                  marginBottom: '11px',
                }}
              >
                <div
                  style={{
                    width: '38.7px',
                    height: '38.7px',
                    borderRadius: '50%',
                    background: c,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '0.76rem',
                    fontWeight: '700',
                    flexShrink: 0,
                  }}
                >
                  {ini2(member)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '0.77rem',
                      fontWeight: '700',
                      color: C.teal,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayName(member)}
                  </div>
                  <div
                    style={{
                      fontSize: '0.63rem',
                      color: C.muted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {member.email || '-'}
                  </div>
                </div>
              </div>

              <div style={{ height: '1px', background: G.soft, marginBottom: '9.2px' }} />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5.5px',
                  fontSize: '0.68rem',
                  color: C.sub,
                }}
              >
                <div style={rowWrap}>
                  <span style={rowLabel}>Role</span>
                  <span
                    style={{
                      padding: '1.8px 7.4px',
                      borderRadius: '20px',
                      fontSize: '0.6rem',
                      fontWeight: '700',
                      background: 'rgba(17,85,80,0.08)',
                      color: C.teal,
                      border: '1px solid rgba(17,85,80,0.18)',
                    }}
                  >
                    {member.role || 'staff'}
                  </span>
                </div>
                <div style={rowWrap}>
                  <span style={rowLabel}>Status</span>
                  <StatusToggle row={member} small />
                </div>
                {member.agency_name && (
                  <div style={rowWrap}>
                    <span style={rowLabel}>Agency</span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2.8px',
                        padding: '1.8px 6.5px',
                        borderRadius: '5.5px',
                        fontSize: '0.6rem',
                        fontWeight: '600',
                        background: C.limeDim,
                        color: '#4a5e00',
                        border: `1px solid ${C.limeBorder}`,
                      }}
                    >
                      <Building2 size={10} />
                      {member.agency_name}
                    </span>
                  </div>
                )}
                {member.created_by_email && (
                  <div style={rowWrap}>
                    <span style={rowLabel}>Added by</span>
                    <span style={{ fontSize: '0.66rem', color: C.sub }}>
                      {member.created_by_email.split('@')[0]}
                    </span>
                  </div>
                )}
                <div style={rowWrap}>
                  <span style={rowLabel}>Joined</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2.8px' }}>
                    <Calendar size={10} style={{ color: C.muted }} />
                    {fmtDate(member.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (loading && members.length === 0) return <StaffListSkeleton viewMode={viewMode} />;

  const hasSel = !!selectedMembers && selectedMembers.length > 0;
  const bulkBtn = (bg: string, border: string, color: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3.7px',
    padding: '4.6px 10.1px',
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: '6.5px',
    color,
    fontSize: '0.67rem',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .members-dt .p-datatable-thead > tr > th { background:#fafbfb !important; color:#9ca3af !important; font-size:0.64rem !important; font-weight:700 !important; text-transform:uppercase !important; letter-spacing:0.5px !important; border-bottom:1px solid ${G.soft} !important; padding:10px 13px !important; }
        .members-dt .p-datatable-tbody > tr { transition:background 0.15s ease; }
        .members-dt .p-datatable-tbody > tr:hover { background:rgba(17,85,80,0.03) !important; }
        .members-dt .p-datatable-tbody > tr > td { border-bottom:1px solid ${G.softer} !important; padding:10px 13px !important; }
        .members-dt .p-datatable-tbody > tr:last-child > td { border-bottom:none !important; }
        .members-dt .p-checkbox .p-checkbox-box.p-highlight { background:#115550 !important; border-color:#115550 !important; }
        .members-dt .p-checkbox .p-checkbox-box:hover { border-color:#c8e94b !important; }
        .members-dt .p-datatable-emptymessage > td { padding:0 !important; text-align:center !important; border-bottom:none !important; }
        .mem-sel:focus { outline:none; }
        .mem-inp::placeholder { color:rgba(17,85,80,0.35); }
        .mem-search-box:focus-within { border-color: rgba(17,85,80,0.35) !important; }
      `}</style>

      <AddStaffForm
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSuccess={() => {
          setSelectedMembers(null);
          if (page === 1) fetchMembers();
          else setPage(1);
        }}
      />

      <EditStaffForm
        open={!!editingStaff}
        staff={editingStaff}
        onClose={() => setEditingStaff(null)}
        onSuccess={() => {
          setEditingStaff(null);
          fetchMembers();
        }}
      />

      <ViewStaffModal
        open={!!viewingId}
        staffId={viewingId}
        onClose={() => setViewingId(null)}
        onEdit={(staff: StaffDetail) => {
          setViewingId(null);
          setEditingStaff(staff);
        }}
        onResetPassword={(staff: StaffDetail) => {
          setViewingId(null);
          setResettingStaff(staff);
        }}
        onChanged={fetchMembers}
      />

      <ResetPasswordModal
        open={!!resettingStaff}
        staff={resettingStaff}
        onClose={() => setResettingStaff(null)}
        onSuccess={() => setResettingStaff(null)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {error && (
          <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />
        )}

        {/* ── Header card ───────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div
            style={{
              ...INNER,
              padding: '12px 16.6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '9.2px',
            }}
          >
            <div>
              <div style={LABEL}>Staff</div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '9.2px', marginTop: '3.7px' }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontSize: '1.01rem',
                    fontWeight: 700,
                    color: '#111827',
                    letterSpacing: '-0.2px',
                  }}
                >
                  All Members
                </h1>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3.7px',
                    padding: '1.8px 8.3px',
                    borderRadius: '20px',
                    background: 'rgba(17,85,80,0.07)',
                    border: '1px solid rgba(17,85,80,0.18)',
                    color: C.tealMid,
                    fontSize: '0.63rem',
                    fontWeight: 600,
                  }}
                >
                  <Contact size={10} />
                  {pagination.total} total
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '1.8px',
                  background: G.shellBg,
                  borderRadius: '7.4px',
                  padding: '2.8px',
                  border: `1px solid ${G.line}`,
                }}
              >
                {(
                  [
                    { m: 'list', i: List, l: 'List' },
                    { m: 'cards', i: LayoutGrid, l: 'Cards' },
                  ] as const
                ).map((v) => (
                  <button
                    key={v.m}
                    onClick={() => setViewMode(v.m)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3.7px',
                      padding: '3.7px 9.2px',
                      borderRadius: '5.5px',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: viewMode === v.m ? C.teal : 'transparent',
                      color: viewMode === v.m ? 'white' : 'rgba(17,85,80,0.55)',
                      border: viewMode === v.m ? `1px solid ${C.teal}` : '1px solid transparent',
                    }}
                  >
                    <v.i size={10} />
                    {v.l}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAddForm(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4.6px',
                  padding: '6.5px 13.8px',
                  background: 'rgb(17,85,80)',
                  border: 'none',
                  borderRadius: '7.4px',
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(17,85,80,0.25)',
                  transition: 'all 0.2s',
                  letterSpacing: '0.2px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(17,85,80,0.4)';
                  e.currentTarget.style.background = 'rgb(22,112,106)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(17,85,80,0.25)';
                  e.currentTarget.style.background = 'rgb(17,85,80)';
                }}
              >
                <Plus size={11} /> ADD MEMBER
              </button>
            </div>
          </div>
        </div>

        {/* ── Filter card ───────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div
            style={{
              ...INNER,
              padding: '9.2px 11px',
              display: 'flex',
              alignItems: 'center',
              gap: '7.4px',
              flexWrap: 'wrap',
            }}
          >
            <div
              className="mem-search-box"
              style={{
                flex: '1 1 166px',
                minWidth: '148px',
                display: 'flex',
                alignItems: 'center',
                background: 'white',
                border: `1px solid ${G.line}`,
                borderRadius: '7.4px',
                height: '33px',
                padding: '0 9.2px',
                gap: '6.5px',
                transition: 'border-color 0.2s',
              }}
            >
              <Search size={11} style={{ color: C.muted }} />
              <input
                className="mem-inp"
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search name, email…"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: C.teal,
                  fontSize: '0.71rem',
                }}
              />
            </div>
            <button
              onClick={handleSearch}
              style={{
                height: '33px',
                padding: '0 13px',
                background: C.lime,
                border: 'none',
                borderRadius: '7.4px',
                color: C.teal,
                fontSize: '0.68rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '3.7px',
                boxShadow: '0 2px 7px rgba(200,233,75,0.3)',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 3px 10px rgba(200,233,75,0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 7px rgba(200,233,75,0.3)';
              }}
            >
              <Search size={10} /> SEARCH
            </button>
            {(tempSearch || search || agencyFilter) && (
              <button
                onClick={handleReset}
                style={{
                  height: '33px',
                  padding: '0 10.1px',
                  background: G.shellBg,
                  border: `1px solid ${G.line}`,
                  borderRadius: '7.4px',
                  color: C.sub,
                  fontSize: '0.66rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2.8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(17,85,80,0.08)';
                  e.currentTarget.style.color = C.teal;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = G.shellBg;
                  e.currentTarget.style.color = C.sub;
                }}
              >
                <X size={10} /> RESET
              </button>
            )}
          </div>
        </div>

        {/* ── Table card ────────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div style={{ ...INNER, overflow: 'hidden' }}>
            <div
              style={{
                padding: '11px 16.6px',
                borderBottom: `1px solid ${G.soft}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '9.2px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
                <div
                  style={{
                    width: '3px',
                    height: '14.8px',
                    background: `linear-gradient(180deg,${C.lime},#8aaa00)`,
                    borderRadius: '3px',
                  }}
                />
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>
                  Members Summary
                </span>
                {hasSel && (
                  <span
                    style={{
                      padding: '1.8px 6.5px',
                      borderRadius: '20px',
                      fontSize: '0.61rem',
                      fontWeight: 700,
                      background: C.limeDim,
                      color: '#4a5e00',
                      border: `1px solid ${C.limeBorder}`,
                    }}
                  >
                    {selectedMembers!.length} selected
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6.5px', alignItems: 'center', flexWrap: 'wrap' }}>
                {hasSel && (
                  <>
                    <button style={bulkBtn('rgba(22,163,74,0.1)', 'rgba(22,163,74,0.3)', '#15803d')}>
                      <Mail size={11} /> EMAIL ({selectedMembers!.length})
                    </button>
                    <button
                      style={bulkBtn('rgba(220,38,38,0.08)', 'rgba(220,38,38,0.25)', '#dc2626')}
                      onClick={async () => {
                        if (
                          !selectedMembers?.length ||
                          !window.confirm(
                            `Delete ${selectedMembers.length} staff member(s)? Leads will be unassigned.`
                          )
                        )
                          return;
                        try {
                          setError(null);
                          await Promise.all(
                            selectedMembers.map((m) => apiClient.delete(`/v1/staff/${m.id}`))
                          );
                          setSelectedMembers(null);
                          await fetchMembers();
                        } catch (e: any) {
                          setError(e.response?.data?.error || 'Failed to delete some staff');
                          await fetchMembers();
                        }
                      }}
                    >
                      <Trash2 size={11} /> DELETE ({selectedMembers!.length})
                    </button>
                  </>
                )}
              </div>
            </div>

            {viewMode === 'list' ? (
              <div style={{ overflowX: 'auto' }}>
                <DataTable
                  value={members}
                  selection={selectedMembers}
                  onSelectionChange={(e: any) => setSelectedMembers(e.value)}
                  dataKey="id"
                  loading={loading}
                  emptyMessage={emptyState}
                  className="members-dt"
                  style={{ fontSize: '0.76rem' }}
                >
                  <Column
                    selectionMode="multiple"
                    headerStyle={{ ...CH, width: '2.8rem' }}
                    bodyStyle={CB}
                  />
                  <Column
                    field="email"
                    header="Member"
                    body={memberTpl}
                    headerStyle={{ ...CH, minWidth: '184px' }}
                    bodyStyle={CB}
                  />
                  <Column field="role" header="Role" body={roleTpl} headerStyle={CH} bodyStyle={CB} />
                  <Column
                    field="status"
                    header="Status"
                    body={statusTpl}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="agency_name"
                    header="Agency"
                    body={agencyTpl}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="created_by_email"
                    header="Added By"
                    body={createdByTpl}
                    headerStyle={{ ...CH, minWidth: '138px' }}
                    bodyStyle={CB}
                  />
                  <Column
                    field="created_at"
                    header="Joined"
                    body={(r) => (
                      <span
                        style={{
                          color: C.sub,
                          fontSize: '0.72rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3.7px',
                        }}
                      >
                        <Calendar size={10} style={{ color: C.muted }} />
                        {fmtDate(r.created_at)}
                      </span>
                    )}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    header="Actions"
                    body={actionTpl}
                    headerStyle={{ ...CH, width: '9.2rem', textAlign: 'center' }}
                    bodyStyle={{ ...CB, textAlign: 'center' }}
                  />
                </DataTable>
              </div>
            ) : (
              <CardsView />
            )}

            <CustomPaginator
              page={page}
              totalPages={pagination.pages}
              totalRecords={pagination.total}
              pageSize={limit}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              showInfoBar
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Members;